<script setup lang="ts">
import { defineRule, Field as VeeField } from 'vee-validate'

import { requiredValidator } from '@/utils/rulesOfValidate'

import { DeliveryMethods } from '@/types/api/api-v3/basket'
import type { BasketPayment } from '@/types/api/api-v3/paymentMethods'
import {
  PaymentMethodDescriptions,
  PaymentMethodType,
} from '@/types/api/api-v3/paymentMethods'

import BasketPaymentChange from '@/components/Basket/basket-payment/BasketPaymentChange.vue'
import BasketErrorMessage from '@/components/Basket/basket-utils/BasketErrorMessage.vue'
const BasketFailed = defineAsyncComponent(
  () => import('@/components/Basket/basket-utils/BasketFailed.vue'),
)

type Props = {
  methodDelivery: DeliveryMethods
  selectedPaymentMethod: BasketPayment | null
  change: number
  amountToPay: number
  isChangeExist: boolean
  changeErrorShow: boolean
  validateErrors: string[]
}
const props = defineProps<Props>()

const emit = defineEmits(['update:change'])

defineRule('required-payment-default', requiredValidator)

// Формируем описание для способа оплат
const descriptionBasketPaymentMethod = computed(() => {
  if (!props.selectedPaymentMethod) return ''

  // Для оплаты картой курьеру и наличными формируем префикса в зависимости от способа доставки
  if (
    [PaymentMethodType.CARD, PaymentMethodType.CASH].includes(
      props.selectedPaymentMethod?.type,
    )
  ) {
    const prefix =
      props.methodDelivery === DeliveryMethods.COURIER ? '_courier' : ''

    return PaymentMethodDescriptions[
      `${props.selectedPaymentMethod?.type}${prefix}`
    ]
  }
  // Для сохраненной карты
  if (checkSavedCard(props.selectedPaymentMethod))
    return PaymentMethodDescriptions.saved_card

  return PaymentMethodDescriptions[props.selectedPaymentMethod?.type]
})

// Проверка на отображение блока сдачи:
// - Способ оплаты выбран
// - Способ оплаты - наличные
// - ошибка с api отсутствует
const isVisibleChangeBlock = computed(() => {
  return (
    props.selectedPaymentMethod?.type === PaymentMethodType.CASH &&
    !props.changeErrorShow
  )
})

const onGetChange = () => {
  emit('update:change')
}

// Ошибка если не существует списка сдач, способ оплаты, и выбран способ оплаты наличные
const checkErrorMessage = computed(() => {
  return (
    props.change !== 0 &&
    !props.isChangeExist &&
    props.selectedPaymentMethod?.type === PaymentMethodType.CASH
  )
})

const changeErrorMessage = computed(() => {
  if (!props.isChangeExist && props.change <= props.amountToPay) {
    return 'Купюра для выдачи должна быть больше суммы заказа'
  } else if (!props.isChangeExist) {
    return 'Сумма корзины изменилась, укажите, пожалуйста, сдачу'
  }

  return ''
})

const isValidateError = computed(() => props.validateErrors.length)
</script>

<template>
  <transition-group name="height" class="payment-methods-info" tag="div">
    <basket-failed
      v-if="changeErrorShow"
      error-message="Не удалось загрузить выбор сдачи"
      @update="onGetChange"
    />

    <div v-if="!isValidateError" class="payment-methods-info__description">
      <p
        v-if="descriptionBasketPaymentMethod"
        class="payment-methods-info__description-text"
      >
        {{ descriptionBasketPaymentMethod }}
      </p>

      <basket-payment-change
        v-if="isVisibleChangeBlock"
        id="change"
        :change="change"
        class="payment-methods-info__change"
      />
    </div>
  </transition-group>

  <transition v-if="checkErrorMessage || isValidateError" name="height">
    <!-- провайдер для скролла до блока сдачи в случае если купюра для сдачи меньше суммы заказа-->
    <vee-field vid="change" name="change" rules="required-payment-default">
      <basket-error-message
        class="payment-methods-info__payment-error validation__error"
        :error-message="changeErrorMessage || validateErrors[0]"
      />
    </vee-field>
  </transition>
</template>

<style scoped lang="scss">
.payment-methods-info {
  &__description {
    display: flex;
    gap: 0 20px;
    align-items: center;
    justify-content: space-between;

    @include media('<tablet') {
      flex-direction: column-reverse;
      gap: 16px;
    }
  }

  &__description-text {
    margin-block-start: 0 !important;
    margin-block-end: 0 !important;
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    color: var(--ui-text-secondary);
    white-space: break-spaces;
  }

  &__change {
    min-width: 140px;

    @include media('<=portrait-phone') {
      width: 100% !important;
    }
  }

  &__payment-error {
    margin-top: 20px;
  }
}
</style>
